import React, { useEffect, useMemo, useRef, useState } from "react";
import { classNames } from "primereact/utils";
import { IKnowledgeArticleSectionModel, SECTION_TYPE } from "../../Services/KnowledgeArticleSectionService";
import { IMediaAssetModel, MEDIA_TYPE, mediaAspectRatio, photoDisplayUrl } from "../../Services/MediaAssetService";
import { resolveInlineMediaImages } from "../../Util/resolveInlineMediaImages";
import { sanitizeRichTextHtml } from "../../Util/sanitizeRichTextHtml";
import DeferredVideoPlayer from "./DeferredVideoPlayer";
import { knowledgeArticleSectionDomId } from "./knowledgeBaseConstants";
import "./ArticleSectionViewer.scss";

interface ArticleSectionViewerProps {
    section: IKnowledgeArticleSectionModel;
    /** True while the section-anchor pulse is in its “on” frame. */
    highlighted?: boolean;
    /** Every media asset of the article (signed URLs included). The section picks out its own items; a
     *  text section resolves its inline images against the whole list. */
    mediaAssets: IMediaAssetModel[];
    /** True while that list is still on its way. The section type already says a photo/video/slideshow is
     *  coming, so its frame is drawn as a skeleton instead of nothing — otherwise the whole card drops in
     *  afterwards and pushes the rest of the article down. */
    mediaLoading?: boolean;
}

/** The frame every photo and video sits in: the space is taken before anything is in it. The asset's own
 *  resolution shapes it where that is known (stored at upload), so a portrait photo is not sitting in a
 *  landscape box; the 16/9 from the stylesheet is what is left for media from before that, and for the frame
 *  drawn while the media list is still coming. */
const MediaFrame: React.FC<{ children: React.ReactNode; asset?: IMediaAssetModel; slideshow?: boolean }> = ({
    children,
    asset,
    slideshow = false,
}) => {
    const ratio = asset ? mediaAspectRatio(asset) : undefined;
    return (
        <div
            className={classNames(
                "article-section-viewer__media",
                slideshow && "article-section-viewer__media--slideshow"
            )}
            style={ratio ? { aspectRatio: ratio } : undefined}
        >
            {children}
        </div>
    );
};

// Not PrimeReact's Skeleton: that one is painted in the app's tertiary colour, which flashes purple over a
// photo frame. Slate, like the frame it fills.
const MediaSkeleton: React.FC = () => (
    <span className="article-section-viewer__media-skeleton" aria-hidden />
);

/** Photo in its frame, with the skeleton on top until the bytes are in. Keyed on the URL itself, so a
 *  refreshed signed URL or the next slide starts over without a reset of its own. */
const SectionImage: React.FC<{ asset: IMediaAssetModel; alt?: string; slideshow?: boolean }> = ({
    asset,
    alt,
    slideshow = false,
}) => {
    const src = photoDisplayUrl(asset);
    const [loadedSrc, setLoadedSrc] = useState<string | undefined>(undefined);
    const loaded = Boolean(src) && loadedSrc === src;

    return (
        <MediaFrame asset={asset} slideshow={slideshow}>
            <img
                className="article-section-viewer__media-image"
                src={src}
                alt={alt ?? ""}
                onLoad={() => setLoadedSrc(src)}
                onError={() => setLoadedSrc(src)}
            />
            {!loaded && <MediaSkeleton />}
        </MediaFrame>
    );
};

/** Own local slide index — a page can have more than one SLIDESHOW section, so this can't be hoisted to a
 *  single page-level state. */
const SectionSlideshow: React.FC<{ assets: IMediaAssetModel[] }> = ({ assets }) => {
    const [slideIndex, setSlideIndex] = useState(0);

    useEffect(() => setSlideIndex(0), [assets.length]);

    const current = assets[slideIndex];
    if (!current) {
        return null;
    }

    return (
        <div className="article-section-viewer__slideshow">
            {current.mediaType === MEDIA_TYPE.VIDEO ? (
                <MediaFrame key={current.uuid} asset={current} slideshow>
                    <DeferredVideoPlayer asset={current} />
                </MediaFrame>
            ) : (
                <SectionImage key={current.uuid} asset={current} slideshow />
            )}
            {assets.length > 1 && (
                <div className="article-section-viewer__slideshow-controls">
                    <button type="button" onClick={() => setSlideIndex((index) => (index - 1 + assets.length) % assets.length)}>
                        <i className="pi pi-chevron-left" />
                    </button>
                    <span>
                        {slideIndex + 1} / {assets.length}
                    </span>
                    <button type="button" onClick={() => setSlideIndex((index) => (index + 1) % assets.length)}>
                        <i className="pi pi-chevron-right" />
                    </button>
                </div>
            )}
        </div>
    );
};

const ArticleSectionViewer: React.FC<ArticleSectionViewerProps> = ({
    section,
    mediaAssets,
    highlighted = false,
    mediaLoading = false,
}) => {
    const textRef = useRef<HTMLDivElement>(null);
    const sectionId = section.uuid ? knowledgeArticleSectionDomId(section.uuid) : undefined;
    const rootClassName = classNames(
        "article-section-viewer",
        section.sectionType === SECTION_TYPE.TEXT && "article-section-viewer--text",
        highlighted && "article-section-viewer--blink",
    );

    const assets = useMemo(() => mediaAssets.filter((asset) => asset.sectionUuid === section.uuid), [mediaAssets, section.uuid]);
    const sanitizedContent = useMemo(() => sanitizeRichTextHtml(section.content), [section.content]);

    useEffect(() => {
        if (section.sectionType === SECTION_TYPE.TEXT) {
            resolveInlineMediaImages(textRef.current, mediaAssets);
        }
    }, [section.sectionType, sanitizedContent, mediaAssets]);

    const showTitle = section.showTitle !== false && !!section.title;
    const title = showTitle ? <h2 className="article-section-viewer__title">{section.title}</h2> : null;

    if (section.sectionType === SECTION_TYPE.TEXT) {
        return (
            <div id={sectionId} className={rootClassName}>
                {title}
                <div
                    ref={textRef}
                    className="article-section-viewer__text quill-content"
                    dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
            </div>
        );
    }

    // Nothing to show yet: the frame while the media list is still coming, nothing once it turned out to
    // be an empty section.
    if (assets.length === 0) {
        if (!mediaLoading) {
            return null;
        }
        return (
            <div id={sectionId} className={rootClassName}>
                {title}
                <MediaFrame>
                    <MediaSkeleton />
                </MediaFrame>
            </div>
        );
    }

    if (section.sectionType === SECTION_TYPE.SLIDESHOW) {
        return (
            <div id={sectionId} className={rootClassName}>
                {title}
                <SectionSlideshow assets={assets} />
            </div>
        );
    }

    // IMAGE or VIDEO — exactly one item.
    const asset = assets[0];
    return (
        <div id={sectionId} className={rootClassName}>
            {title}
            {asset.mediaType === MEDIA_TYPE.VIDEO ? (
                <MediaFrame asset={asset}>
                    <DeferredVideoPlayer asset={asset} />
                </MediaFrame>
            ) : (
                <SectionImage asset={asset} alt={section.title} />
            )}
        </div>
    );
};

export default ArticleSectionViewer;
